import { useMemo, useState } from 'react';

/**
 * Landing pública — base Stripe, com demo interativa e mídia real.
 *
 * Três decisões vindas da referência (stripe.com/en-br) e da escolha do dono:
 *
 *  1. A assinatura visual é a FAIXA DIAGONAL de gradiente atravessando o herói
 *     — traduzida para as cores quentes da marca, não copiada.
 *  2. No lugar do screenshot estático do Stripe, uma DEMO FUNCIONAL: o
 *     visitante ordena a mesa de ofertas e monta um post ali mesmo, sem
 *     cadastro. Mostrar o produto de verdade converte mais que descrevê-lo.
 *  3. As seções usam VÍDEOS REAIS de 3–7s do painel (gravados do app rodando,
 *     não mockups) — quem chega vê exatamente o que vai receber.
 */

type Props = {
  onLogin: () => void;
  onRegister: () => void;
};

/* ------------------------------------------------------------- demo dados */

type OfertaDemo = {
  nome: string;
  categoria: string;
  taxa: number;
  comissao: number;
  vendas: number;
};

/** Dados REAIS da mesa (colhidos da API em 25/08) — a demo não inventa. */
const OFERTAS_DEMO: OfertaDemo[] = [
  { nome: 'Kit Maca Power Karseell 500g', categoria: 'Cabelo', taxa: 16, comissao: 7.84, vendas: 23725 },
  { nome: 'Truss Leave-in Reconstrutor', categoria: 'Cabelo', taxa: 18, comissao: 9.0, vendas: 5407 },
  { nome: 'Cheirinho para Carro Árabe', categoria: 'Aromas', taxa: 28, comissao: 5.24, vendas: 3716 },
  { nome: 'Massageador de Pescoço 3D', categoria: 'Cuidados', taxa: 28, comissao: 33.32, vendas: 214 },
  { nome: 'Kit Mobiliador Gamer RGB', categoria: 'Setup', taxa: 43, comissao: 27.91, vendas: 3 },
];

type OrdemDemo = 'vendas' | 'taxa' | 'comissao';

const brl = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/* ------------------------------------------------------- demo interativa */

/**
 * A mesa que funciona dentro do herói.
 *
 * É deliberadamente o MESMO gesto do produto: ordenar pela coluna que importa
 * e clicar em Selecionar. Ao selecionar, o post aparece montado — com legenda
 * e link por canal — que é exatamente o que o app entrega.
 */
function DemoInterativa() {
  const [ordem, setOrdem] = useState<OrdemDemo>('vendas');
  const [escolhida, setEscolhida] = useState<OfertaDemo | null>(null);

  const linhas = useMemo(
    () => [...OFERTAS_DEMO].sort((a, b) => b[ordem] - a[ordem]),
    [ordem],
  );

  const cab: Array<{ id: OrdemDemo; rotulo: string }> = [
    { id: 'taxa', rotulo: '%' },
    { id: 'comissao', rotulo: 'Comissão' },
    { id: 'vendas', rotulo: 'Vendas' },
  ];

  return (
    <div className="ldemo" aria-label="Demonstração interativa da mesa de ofertas">
      <div className="ldemo-head">
        <span className="ldemo-dot" aria-hidden="true" />
        <span>Mesa de ofertas — experimente</span>
        <span className="ldemo-hint">clique nas colunas e em Selecionar</span>
      </div>

      <table>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Oferta</th>
            {cab.map((c) => (
              <th key={c.id}>
                <button
                  type="button"
                  aria-current={ordem === c.id ? 'true' : undefined}
                  onClick={() => setOrdem(c.id)}
                >
                  {c.rotulo}
                  {ordem === c.id && ' ↓'}
                </button>
              </th>
            ))}
            <th aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {linhas.map((o) => (
            <tr key={o.nome} className={escolhida?.nome === o.nome ? 'is-picked' : ''}>
              <td>
                <span className="ldemo-nome">{o.nome}</span>
                <span className="ldemo-cat">{o.categoria}</span>
              </td>
              <td className="num">{o.taxa}%</td>
              <td className="num">
                <strong>{brl(o.comissao)}</strong>
              </td>
              <td className="num">{o.vendas.toLocaleString('pt-BR')}</td>
              <td>
                <button
                  type="button"
                  className="ldemo-pick"
                  onClick={() => setEscolhida(o)}
                >
                  Selecionar
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* O resultado do gesto: o post montado, como o app monta de verdade. */}
      <div className={`ldemo-post${escolhida ? ' is-on' : ''}`} aria-live="polite">
        {escolhida ? (
          <>
            <div className="ldemo-post-msg">
              <p>
                Olha esse achadinho: <strong>{escolhida.nome}</strong> 😍
                <br />
                Comissão de {escolhida.taxa}% pra quem divulga — corre que é por
                tempo limitado!
              </p>
              <span className="mono">s.shopee.com.br/8fRqKb…</span>
            </div>
            <div className="ldemo-post-meta">
              <span className="badge ok">agendado · hoje 19:30</span>
              <span>WhatsApp e Instagram, cada um com seu link de comissão</span>
            </div>
          </>
        ) : (
          <span className="ldemo-post-vazio">
            Selecione uma oferta e veja o post pronto aparecer aqui.
          </span>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- página */

const VIDEOS = [
  {
    src: '/landing/v1-ofertas.mp4',
    poster: '/landing/v1-ofertas-poster.jpg',
    kicker: 'Garimpo',
    titulo: 'Pare de caçar oferta. Compare e escolha.',
    texto:
      'A varredura traz os produtos da Shopee com o que decide a divulgação: comissão, ' +
      'Comissão Xtra, vendas e potencial — lado a lado, numa tabela de comparar. ' +
      'Um clique ordena pela coluna que importa; outro gera o seu link de afiliado.',
  },
  {
    src: '/landing/v2-producao.mp4',
    poster: '/landing/v2-producao-poster.jpg',
    kicker: 'Produção',
    titulo: 'O post pronto, do seu jeito, para cada rede.',
    texto:
      'Instagram, WhatsApp e TikTok têm caixas separadas: cada um com sua mídia, seu texto ' +
      'e sua prévia de como vai aparecer. A legenda sai no seu formato — gancho, benefícios, ' +
      'chamada para comentar — com a identificação de publicidade no lugar certo.',
  },
  {
    src: '/landing/v3-grafico.mp4',
    poster: '/landing/v3-grafico-poster.jpg',
    kicker: 'Resultado',
    titulo: 'Quanto rendeu, dia a dia, rede por rede.',
    texto:
      'As conversões chegam da Shopee já atribuídas ao canal que vendeu, com as canceladas ' +
      'fora da soma. Comissão por dia, comparação entre redes e os produtos que mais giram — ' +
      'o extrato que fecha a conta do seu esforço.',
  },
];

export function Landing({ onLogin, onRegister }: Props) {
  return (
    <div className="landing">
      {/* A faixa diagonal: a assinatura do Stripe nas cores quentes da marca.
          Fica atrás de tudo no herói; o conteúdo flutua sobre ela. */}
      <div className="landing-ribbon" aria-hidden="true" />


      <header className="landing-top">
        <div className="brand">
          <img src="/logo-192.png" alt="" className="brand-mark" width={24} height={24} />
          Automação Shopee
        </div>
        <div className="landing-top-actions">
          <button type="button" className="landing-btn-outline" onClick={onLogin}>
            Entrar
          </button>
          <button type="button" className="primary" onClick={onRegister}>
            Criar conta
          </button>
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <h1>
              Sua operação de afiliado,
              <br />
              <span className="landing-grad-text">no automático.</span>
            </h1>
            <p className="landing-lede">
              A plataforma garimpa as ofertas da Shopee, monta o post com o seu texto e
              publica sozinha no Instagram e no WhatsApp — cada rede com o próprio link,
              para o relatório dizer exatamente qual delas vendeu.
            </p>
            <div className="landing-hero-cta">
              <button type="button" className="primary" onClick={onRegister}>
                Começar agora →
              </button>
              <button type="button" className="landing-btn-outline" onClick={onLogin}>
                Já tenho conta
              </button>
            </div>
          </div>

          {/* A mascote ocupa a coluna inteira, em fluxo — não mais absoluta atrás
              do cartão. Ela grande E inteira não cabe no mesmo bloco que a
              demo: um sempre cobre o outro. Então a demo desceu para a seção
              seguinte e este lado é só dela. */}
          <div className="landing-mascote" aria-hidden="true" />
        </section>

        {/* A demo ganhou bloco próprio, centralizada e maior. */}
        <section className="landing-demo-secao">
          <p className="landing-demo-kicker mono">Experimente sem cadastro</p>
          <h2>Ordene pela coluna que importa e monte o post.</h2>
          <DemoInterativa />
        </section>

        {/* Barra de integrações: o equivalente honesto da barra de logos. */}
        <div className="landing-logos">
          <span>Conectado a</span>
          <strong>Shopee Afiliados</strong>
          <strong>Instagram</strong>
          <strong>WhatsApp</strong>
          <em>TikTok em breve</em>
        </div>

        {/* ------------------------------------------------ vídeos reais */}
        <section className="landing-sec">
          <h2>É assim por dentro. De verdade.</h2>
          <p className="landing-sec-lede">
            Nada aqui é maquete: os vídeos abaixo são o painel rodando, gravado da tela.
          </p>
          <div className="landing-media">
            {VIDEOS.map((v) => (
              // Lados alternados via CSS (nth-child): vídeo à direita no
              // primeiro, à esquerda no segundo, e assim por diante.
              <figure key={v.src}>
                <figcaption>
                  <span className="mono">{v.kicker}</span>
                  <strong>{v.titulo}</strong>
                  <span>{v.texto}</span>
                </figcaption>
                {/* Loop mudo e curto: é ilustração viva, não player. O poster
                    evita o quadro branco enquanto carrega. */}
                <video
                  src={v.src}
                  poster={v.poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                />
              </figure>
            ))}
          </div>
        </section>

        {/* ------------------------------------------------ três passos */}
        <section className="landing-sec">
          <h2>Do garimpo ao extrato, num trilho só</h2>
          <div className="landing-passos">
            <div>
              <span className="mono">01</span>
              <h3>Ofertas</h3>
              <p>
                Varredura automática no intervalo que você definir, só com o que passa do
                seu piso de comissão.
              </p>
            </div>
            <div>
              <span className="mono">02</span>
              <h3>Produção e agenda</h3>
              <p>
                Post avulso ou programação recorrente — “às 9h e às 19h30, por 30 dias ou
                sem prazo”. O robô publica no horário, todo dia.
              </p>
            </div>
            <div>
              <span className="mono">03</span>
              <h3>Desempenho</h3>
              <p>
                Cada venda chega atribuída à rede que a gerou. Você vê o que rendeu e
                decide onde investir esforço.
              </p>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------- CTA */}
        <section className="landing-fim">
          <h2>Amanhã às 07:00 a varredura roda. Com ou sem você.</h2>
          <button type="button" className="primary" onClick={onRegister}>
            Criar minha conta
          </button>
        </section>

        {/* Fecho: o único lugar onde a arte aparece nítida e no tamanho dela. */}
        <section className="landing-fecho">
          <div>
            <h2>
              Sua próxima oferta já está
              <br />
              esperando para ser postada.
            </h2>
            <p>
              Conecte a conta da Shopee, escolha os grupos e deixe a fila rodar. Você
              volta no fim do dia para ver quanto rendeu — por rede, com as canceladas
              já fora da conta.
            </p>
            <div className="landing-fecho-acoes">
              <button type="button" className="primary" onClick={onRegister}>
                Criar conta →
              </button>
              <button type="button" className="landing-btn-outline" onClick={onLogin}>
                Entrar
              </button>
            </div>
          </div>
          <div className="landing-fecho-arte" aria-hidden="true" />
        </section>
      </main>

      <footer className="landing-foot">
        <span className="mono">Automação Shopee</span>
        <span className="muted">
          Divulgação com identificação de publicidade, como o CONAR recomenda.
        </span>
        {/* Link real, não botão: a Meta abre esta URL direto, em janela anônima,
            durante a análise do app — e precisa ser copiável e indexável. */}
        <a href="/privacidade" className="muted" style={{ textDecoration: 'underline' }}>
          Política de Privacidade
        </a>
        <a href="/termos" className="muted" style={{ textDecoration: 'underline' }}>
          Termos de Serviço
        </a>
      </footer>
    </div>
  );
}
